import { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import { Bold, Italic, Underline, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import type { EmailProvider } from '@/lib/email/types';
import { TIMEZONE_OPTIONS } from '@/lib/constants';
import { CampaignSchedule } from '@/components/email/campaigns/CampaignSchedule';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { captureError } from '@/lib/sentry';

interface SenderSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: EmailProvider;
  onSuccess?: () => void;
}

export function SenderSettingsDialog({
  open,
  onOpenChange,
  provider,
  onSuccess,
}: SenderSettingsDialogProps) {
  const [displayName, setDisplayName] = useState(provider.display_name || '');
  const [physicalAddress, setPhysicalAddress] = useState(provider.physical_address || '');
  const [timezone, setTimezone] = useState(provider.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [timezoneTouched, setTimezoneTouched] = useState(false);
  // Normalize live values for the form: PostgREST returns `time with time zone`
  // as '14:00:00-06:00' (rejected by <input type="time">), and the legacy seeder
  // stores day names capitalized while CampaignSchedule uses lowercase ids.
  // NULL window fields render EMPTY ('no restriction'), never injected defaults —
  // saving a signature must not silently impose a 09:00-17:00 window.
  const normalizeWindow = (p: EmailProvider) => ({
    allowedDays: (p.allowed_days ?? []).map((d: string) => d.toLowerCase()),
    startTime: (p.start_time ?? '').slice(0, 5),
    endTime: (p.end_time ?? '').slice(0, 5),
  });
  const [emailSettings, setEmailSettings] = useState(() => normalizeWindow(provider));
  const [tracking, setTracking] = useState(() => provider.provider_settings || {
    trackOpens: true,
    trackClicks: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-4',
        },
      }),
    ],
    content: provider.signature || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] border rounded-md p-3',
      },
    },
  });

  useEffect(() => {
    if (open && editor) {
      editor.commands.setContent(provider.signature || '');
      setDisplayName(provider.display_name || '');
      setPhysicalAddress(provider.physical_address || '');
      setTimezone(provider.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setEmailSettings(normalizeWindow(provider));
      setTracking(provider.provider_settings || { trackOpens: true, trackClicks: true });
    }
  }, [open, editor, provider]);

  const handleSave = async () => {
    if (!editor) return;

    const originalStart = (provider.start_time ?? '').slice(0, 5);
    const originalEnd = (provider.end_time ?? '').slice(0, 5);
    const originalDays = (provider.allowed_days ?? []).map((d: string) => d.toLowerCase());
    const windowChanged =
      emailSettings.startTime !== originalStart ||
      emailSettings.endTime !== originalEnd ||
      emailSettings.allowedDays.join(',') !== originalDays.join(',');

    if (windowChanged && !!emailSettings.startTime !== !!emailSettings.endTime) {
      toast({
        title: "Both start and end time are required together",
        description: "Set both, or clear both. A partial window would not restrict sending.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload: Record<string, unknown> = {
        signature: editor.getHTML(),
        display_name: displayName,
        physical_address: physicalAddress.trim() || null,
        provider_settings: tracking,
      };
      // Window fields are send-path controls now (enforced at claim time).
      // Only write them when the user actually changed them, so a signature
      // save can never silently impose (or erase) a delivery window; an
      // empty day list / empty times are saved as NULL = no restriction.
      if (windowChanged) {
        payload.allowed_days = emailSettings.allowedDays.length ? emailSettings.allowedDays : null;
        payload.start_time = emailSettings.startTime || null;
        payload.end_time = emailSettings.endTime || null;
      }
      if (timezoneTouched) payload.timezone = timezone;
      const { error } = await supabase
        .from('email_providers')
        .update(payload)
        .eq('id', provider.id);

      if (error) throw error;

      toast({
        description: "Sender settings updated successfully",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating sender settings:', error);
      captureError(error as Error, { feature: 'settings-sender-settings-dialog-handle-save' });
      toast({
        title: "Error",
        description: "Failed to update sender settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addLink = () => {
    const url = window.prompt('URL');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Sender Settings</DialogTitle>
          <DialogDescription>
            Configure display name, signature, and delivery window for {provider.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto">
          <div className="space-y-2">
            <label className="text-sm font-medium">Display Name</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter display name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email Signature</label>
            <div className="border rounded-md">
              <div className="flex items-center gap-1 p-2 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={editor?.isActive('bold') ? 'bg-muted' : ''}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={editor?.isActive('italic') ? 'bg-muted' : ''}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleMark('underline').run()}
                  className={editor?.isActive('underline') ? 'bg-muted' : ''}
                >
                  <Underline className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addLink}
                  className={editor?.isActive('link') ? 'bg-muted' : ''}
                >
                  <Link2 className="h-4 w-4" />
                </Button>
              </div>
              <EditorContent editor={editor} className="p-3" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Mailing address (CAN-SPAM)</label>
            <Textarea
              value={physicalAddress}
              onChange={(e) => setPhysicalAddress(e.target.value)}
              placeholder="e.g. Acme Inc, 123 Main St, Springfield, IL 62704"
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              A valid physical mailing address is required by law to send marketing campaigns. It's
              appended to every campaign email sent from this account.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Timezone</label>
            <Select
              value={timezone}
              onValueChange={(v) => { setTimezone(v); setTimezoneTouched(true); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(tz => tz.value && (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              All scheduled emails will be sent according to this timezone
            </p>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium">Schedule Settings</h3>
            <p className="text-xs text-muted-foreground">
              Campaign emails are only sent inside this window, in the sender's timezone.
              Jobs due outside the window wait for the next window to open.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Active Days</label>
                <CampaignSchedule
                  schedule={emailSettings.allowedDays}
                  onChange={(days) => setEmailSettings(prev => ({
                    ...prev,
                    allowedDays: days
                  }))}
                />
                <p className="text-xs text-muted-foreground">
                  No days selected = no weekday restriction (sends any day).
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Start Time</label>
                  <Input
                    type="time"
                    value={emailSettings.startTime}
                    onChange={(e) => setEmailSettings(prev => ({
                      ...prev,
                      startTime: e.target.value
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">End Time</label>
                  <Input
                    type="time"
                    value={emailSettings.endTime}
                    onChange={(e) => setEmailSettings(prev => ({
                      ...prev,
                      endTime: e.target.value
                    }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium">Tracking Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Track Opens</label>
                  <p className="text-sm text-muted-foreground">Track when recipients open emails</p>
                </div>
                <Switch
                  checked={tracking.trackOpens}
                  onCheckedChange={(checked) => setTracking(prev => ({ ...prev, trackOpens: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Track Clicks</label>
                  <p className="text-sm text-muted-foreground">Track when recipients click links</p>
                </div>
                <Switch
                  checked={tracking.trackClicks}
                  onCheckedChange={(checked) => setTracking(prev => ({ ...prev, trackClicks: checked }))}
                />
              </div>
            </div>
          </div>

        </div>
        <DialogFooter className="border-t pt-4 mt-4">
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}